/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle,
  LinearProgress, Pagination, Paper,
  Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter,
  TableHead, TableRow, Tab, Tabs, Typography, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { brand } from '../../../shared/themes';
import { FaturamentoService, IListagemFaturamento, IGetItensFaturamento, TTipoFaturamento } from '../../../shared/services/api/Financeiro/Faturamento';
import { SessaoCaixaService, ISessaoCaixaAtiva } from '../../../shared/services/api/Financeiro/SessaoCaixa';
import { FerramentasDaListagemFaturamento, IconBadge, LinhasEsqueletoTabela, paperListagemSx, tableContainerListagemSx } from '../../../shared/components';
import { DialogFaturameto } from './DialogFaturamento';
import { Dialogsendnfce } from './Dialogsendnfce';
import { DialogNFE } from './Dialogsendnfe';
import { DialogFaturar } from './DialogsendFaturar';
import { DialogAbrirSessaoCaixa } from './DialogAbrirSessaoCaixa';
import { DialogFecharSessaoCaixa } from './DialogFecharSessaoCaixa';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular, moedaMobile } from '../../../shared/components/listagem-mobile';

const ABAS: { valor: TTipoFaturamento; rotulo: string }[] = [
  { valor: 'venda', rotulo: 'Vendas' },
  { valor: 'entrada', rotulo: 'Entradas' },
  { valor: 'devolucao', rotulo: 'Devoluções' },
  { valor: 'ordemservico', rotulo: 'Ordens de Serviço' },
];

// Duração "02h 45m" desde a abertura da sessão — recalculada a cada minuto
// (ver efeito de `agora` abaixo), não só no primeiro render.
const formatarDuracao = (inicio: string, agora: dayjs.Dayjs): string => {
  const minutosTotais = Math.max(0, agora.diff(dayjs(inicio), 'minute'));
  const horas = Math.floor(minutosTotais / 60);
  const minutos = minutosTotais % 60;
  return `${String(horas).padStart(2, '0')}h ${String(minutos).padStart(2, '0')}m`;
};

export const ListagemFaturamento: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  // No celular esconde Emissão/Número/Operação/CPF (secundárias) em vez de deixar a
  // tabela rolar na horizontal — cabe tudo na tela só com Sel./Parceiro/Total/Status.
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const [rows, setRows] = useState<IListagemFaturamento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTipoRow, setSelectedTipoRow] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalVendas, setTotalVendas] = useState(0);
  const [totalEntradas, setTotalEntradas] = useState(0);
  const [totalDevolucoes, setTotalDevolucoes] = useState(0);
  const [totalOrdensServico, setTotalOrdensServico] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedNome, setSelectedNome] = useState<string>('');
  const [selectedTotal, setSelectedTotal] = useState<number>(0);
  const [selectedNumeroDocumento, setSelectedNumeroDocumento] = useState<number | string>(0);
  const [selectedCpf, setSelectedCpf] = useState<string>('');
  const [selectedEmissaoData, setSelectedEmissaoData] = useState<string>('');
  const [selecteTpag, setSelectedTpag] = useState<number>();
  const [selectedParceiro, setSelectedParceiro] = useState<string | null>(null);
  const [openDialogFaturamento, setOpenDialogFaturamento] = useState(false);
  const [openDialognfe, setOpenDialognfe] = useState(false);
  const [openDialogFaturar, setOpenDialogFaturar] = useState(false);
  const [openDialogPagamento, setOpenDialogPagamento] = useState(false);
  const [movimentoData, setMovimentoData] = useState<IGetItensFaturamento[]>([]);
  const [openConfirmFaturar, setOpenConfirmFaturar] = useState(false);
  const [sessaoAtiva, setSessaoAtiva] = useState<ISessaoCaixaAtiva | null>(null);
  const [carregandoSessao, setCarregandoSessao] = useState(true);
  const [openDialogAbrirSessao, setOpenDialogAbrirSessao] = useState(false);
  const [openDialogFecharSessao, setOpenDialogFecharSessao] = useState(false);
  const [agora, setAgora] = useState(() => dayjs());
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const tipoAba = useMemo(() => {
    return (searchParams.get('tipo') as TTipoFaturamento) || 'venda';
  }, [searchParams]);

  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  // Quantidade de linhas por página é sempre calculada pelo espaço real da
  // tela (sem seletor manual "X / página" — esse controle extra é o que
  // empurrava a listagem pra fora da altura reservada e criava rolagem no
  // rodapé da página sem necessidade).
  const { tabelaRef, containerHeight, rowsPerPage } = useListagemAltura({
    cacheKey: 'faturamento',
    valorInicial: Environment.LIMITE_DE_LINHASSMALL,
    rowHeight: 44,
    footerReservado: 48,
    // Folga entre o fim da listagem e o fim da tela — sem essa margem, o
    // conteúdo encosta exatamente na borda inferior da janela e qualquer
    // imprecisão de poucos pixels (zoom do navegador, barra de rolagem do
    // sistema, etc.) já é suficiente pra criar rolagem na página quando há
    // muitos itens.
    bottomGap: 32,
  });

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      FaturamentoService.getAll(pagina, tipoAba, busca, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setTotalVendas(result.totalVendas);
            setTotalEntradas(result.totalEntradas);
            setTotalDevolucoes(result.totalDevolucoes);
            setTotalOrdensServico(result.totalOrdensServico);
            setRows(result.data);
            setSelectedId('');
          }
        });
    });
  }, [busca, tipoAba, pagina, rowsPerPage, refreshKey]);

  // Trocar a quantidade por página sempre volta pra primeira página, senão a
  // página atual pode ficar fora do novo total de páginas.
  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ tipo: tipoAba, busca, pagina: '1' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage]);

  // Relógio da sessão ("Tempo aberta") — recalcula a cada minuto.
  useEffect(() => {
    const id = setInterval(() => setAgora(dayjs()), 60000);
    return () => clearInterval(id);
  }, []);

  const refreshList = () => {
    setRefreshKey(prev => prev + 1);
    setSelectedId(null);
    setSelectedParceiro(null);
    setSelected('');
  };

  // Sessão de caixa do usuário logado — sempre consultada no backend, nunca
  // guardada em localStorage, pra funcionar de qualquer computador que ele
  // logue. Sem sessão aberta, a tela inteira fica bloqueada (ver render).
  const carregarSessao = () => {
    setCarregandoSessao(true);
    SessaoCaixaService.getAtiva().then((resultado) => {
      setCarregandoSessao(false);
      setSessaoAtiva(resultado instanceof Error ? null : resultado);
    });
  };
  useEffect(() => {
    carregarSessao();
  }, []);

  const handleAtualizar = () => {
    refreshList();
    carregarSessao();
  };

  const handleTrocarAba = (_: React.SyntheticEvent, novoTipo: TTipoFaturamento) => {
    setSearchParams({ tipo: novoTipo, busca, pagina: '1' }, { replace: true });
    setSelectedId(null);
    setSelectedParceiro(null);
    setSelected('');
  };

  const handleFecharDialogAbrirSessao = (sessao?: ISessaoCaixaAtiva) => {
    setOpenDialogAbrirSessao(false);
    if (sessao) setSessaoAtiva(sessao);
  };
  const handleFecharDialogFecharSessao = (value?: string) => {
    setOpenDialogFecharSessao(false);
    if (value === 'true') carregarSessao();
  };
  const formatarCPF = (cpf: string) => {
    if (!cpf) return '';
    const cpfLimpo = cpf.replace(/\D/g, '');
    if (cpfLimpo.length === 11) {
      return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  };
  const handleSelectItem = (
    id: string,
    idparceiro: string,
    nome: string,
    tpag: number,
    total: number,
    numero: number | string,
    emissao: string,
    tipo: number,
    cpf: string
  ) => {
    setSelectedId(prevSelectedId => prevSelectedId === id ? null : id);
    setSelectedParceiro(prev => prev === idparceiro ? null : idparceiro);
    setSelected(id);
    setSelectedNome(nome);
    setSelectedTpag(tpag);
    setSelectedTotal(total);
    setSelectedNumeroDocumento(numero);
    setSelectedEmissaoData(emissao);
    setSelectedTipoRow(tipo);
    setSelectedCpf(cpf);
  };
  const handleRowClick = async (id: string) => {
    try {
      setIsLoading(true);
      const resultado = await FaturamentoService.getByIdFaturamento(id);
      if (resultado instanceof Error) {
        setTms('error');
        setMsg(resultado.message);
        setOpenmsg(true);
      } else if (Array.isArray(resultado)) {
        setMovimentoData(resultado);
        setOpenDialogFaturamento(true);
      } else {
        setTms('warning');
        setMsg('Formato de dados inválido');
        setOpenmsg(true);
      }
    } catch (error) {
      setTms('error');
      setMsg('Erro ao carregar dados do faturamento');
      setOpenmsg(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleFaturarSelecionado = async () => {
    if (!sessaoAtiva) {
      setTms('warning');
      setMsg('Abra uma sessão de caixa antes de faturar');
      setOpenmsg(true);
      return;
    }
    if (!selectedId) {
      setTms('warning');
      setMsg('Selecione um item para faturar');
      setOpenmsg(true);
      return;
    }
    if (selecteTpag === 1) {
      setOpenDialogFaturar(true);
      return;
    }
    if (selecteTpag === 2) {
      setOpenConfirmFaturar(true);
      return;
    }
  };

  const confirmarFaturamento = async () => {
    try {
      setOpenConfirmFaturar(false);
      setIsLoading(true);
      const payload = {
        idmov: selectedId || '',
        datapgto: dayjs().format('YYYY-MM-DD'),
      };
      const result = await FaturamentoService.createfaturarid(payload);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
      } else {
        setTms('success');
        setMsg('Lançamento realizado com sucesso!');
        refreshList();
      }
      setOpenmsg(true);
      setSelectedId('');
    } catch (error) {
      setTms('error');
      setMsg('Erro inesperado ao faturar.');
      setOpenmsg(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSendNFCE = async () => {
    if (!selectedId) {
      setTms('warning');
      setMsg('Selecione um item para faturar');
      setOpenmsg(true);
      return;
    }
    // CPF do consumidor não é mais perguntado antes — a própria dialog de
    // NFC-e tem uma seção opcional pra isso (F6 ou clicar em "CPF na
    // nota"), sem precisar de um passo de confirmação à parte.
    setOpenDialogPagamento(true);
  };
  const handleSendNFE = async () => {
    if (!selectedId) {
      setTms('warning');
      setMsg('Selecione um item para faturar');
      setOpenmsg(true);
      return;
    }
    if (selectedParceiro === Environment.URL_IDPARCEIRO) {
      setTms('error');
      setMsg('Vendas do parceiro à vista não podem emitir NFE, apenas NFC-e.');
      setOpenmsg(true);
      return;
    }
    setOpenDialognfe(true);
  };
  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  return (
    <>

      <LayoutBaseDePagina
        titulo='Faturamento'
        subtitulo='Gerencie vendas, entradas e devoluções'
        barraDeFerramentas={
          <Box sx={{ width: '100%', px: 2, pt: 0, pb: 1, overflowX: 'hidden' }}>
            {/* Altura mínima fixa — o card ocupa o mesmo espaço em qualquer um dos 3
                estados (carregando/ativa/bloqueada). Isso é essencial pro cálculo de altura
                da listagem logo abaixo (useListagemAltura): ele mede a posição da tabela uma
                única vez, na primeira renderização — se esse card mudasse de altura depois
                (ex.: chip pequeno de "bloqueado" virando o card cheio de "sessão ativa"
                assim que os dados chegam), a tabela ficaria maior que o espaço realmente
                disponível e a página inteira passaria a rolar (o bug corrigido aqui). */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1,
                minHeight: 92,
                borderRadius: '14px',
                border: `1px solid ${theme.torq.border.default}`,
                borderLeft: `4px solid ${
                  carregandoSessao
                    ? theme.torq.text.muted
                    : sessaoAtiva
                      ? theme.palette.success.main
                      : theme.palette.warning.main
                }`,
                backgroundColor: theme.torq.surface.card,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              {carregandoSessao ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <IconBadge icon={<PointOfSaleIcon />} tone="neutral" size={40} />
                  <Typography variant="body2" sx={{ color: theme.torq.text.secondary }}>
                    Carregando sessão de caixa...
                  </Typography>
                </Box>
              ) : sessaoAtiva ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconBadge icon={<PointOfSaleIcon />} tone="success" size={40} />
                    <Box>
                      <Typography fontWeight={700} sx={{ color: theme.torq.text.primary }}>
                        Sessão ativa
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.torq.text.secondary }}>
                        {`Sessão ${sessaoAtiva.numero} • Caixa: ${sessaoAtiva.nomeConta}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.torq.text.muted }}>
                        {`Aberta em ${dayjs(sessaoAtiva.dataabertura).format('DD/MM/YY [às] HH:mm')}`}
                        {sessaoAtiva.nomeUsuario ? ` por ${sessaoAtiva.nomeUsuario}` : ''}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.torq.text.muted, display: 'block' }}>
                        Início
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {dayjs(sessaoAtiva.dataabertura).format('DD/MM/YY')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.torq.text.muted, display: 'block' }}>
                        Tempo aberta
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatarDuracao(sessaoAtiva.dataabertura, agora)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.torq.text.muted, display: 'block' }}>
                        Vendas na sessão
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {(sessaoAtiva.vendasNoTurno ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setOpenDialogFecharSessao(true)}
                    >
                      Fechar sessão
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconBadge icon={<LockIcon />} tone="warning" size={40} />
                    <Box>
                      <Typography fontWeight={700} sx={{ color: theme.torq.text.primary }}>
                        Faturamento bloqueado
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.torq.text.secondary }}>
                        Abra uma sessão de caixa pra liberar o faturamento
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PointOfSaleIcon />}
                    onClick={() => setOpenDialogAbrirSessao(true)}
                  >
                    Abrir sessão de caixa
                  </Button>
                </>
              )}
            </Paper>

            <FerramentasDaListagemFaturamento
              aoClicarEmFaturamento={handleFaturarSelecionado}
              aoClicarEmNFCE={handleSendNFCE}
              /* Ordem de Serviço usa o mesmo movimento.tipo=2 de uma venda comum
                 (ver TipoFaturamento.ts/backend), mas os itens ali dentro só
                 cobrem peças — mão de obra nunca vira item de nota. Emitir
                 NFe/NFC-e a partir daqui sairia sem a mão de obra, incompleta,
                 então trava os dois botões nessa aba (só falta "Faturar"). */
              desabilitarNFCE={selectedTipoRow === 1 || tipoAba === 'ordemservico' || !sessaoAtiva}
              desabilitarNFE={selectedTipoRow === 1 || tipoAba === 'ordemservico' || selectedParceiro === Environment.URL_IDPARCEIRO || !sessaoAtiva}
              aoClicarEmNFE={handleSendNFE}
              isLoading={false}
            />

            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                rowGap: 1,
                borderBottom: `1px solid ${theme.torq.border.default}`,
              }}
            >
              {/* minWidth:0 + variant scrollable — sem isso, um Tabs dentro de um flex row
                  nunca encolhe abaixo da largura total dos itens (comportamento padrão de
                  flex item), e numa janela mais estreita esse trecho sozinho já empurrava a
                  tela inteira pra rolar de lado. Com scroll interno próprio (contido aqui),
                  a rolagem lateral nunca escapa pra página. */}
              <Tabs
                value={tipoAba}
                onChange={handleTrocarAba}
                variant='scrollable'
                scrollButtons='auto'
                allowScrollButtonsMobile
                sx={{
                  minHeight: 40,
                  minWidth: 0,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: '0.85rem' },
                }}
              >
                {ABAS.map((aba) => {
                  const contagem = aba.valor === 'venda' ? totalVendas
                    : aba.valor === 'entrada' ? totalEntradas
                      : aba.valor === 'devolucao' ? totalDevolucoes
                        : totalOrdensServico;
                  return (
                    <Tab
                      key={aba.valor}
                      value={aba.valor}
                      label={`${contagem} ${aba.rotulo}`}
                    />
                  );
                })}
              </Tabs>

              <Button
                variant='outlined'
                color='inherit'
                size='small'
                startIcon={<RefreshIcon />}
                onClick={handleAtualizar}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 2, flexShrink: 0 }}
              >
                Atualizar
              </Button>
            </Box>
          </Box>
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openmsg}
          autoHideDuration={null}
          onClose={(event, reason) => {
            if (reason === 'clickaway') {
              return;
            }
          }}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{
              width: '100%',
              maxWidth: '500px',
              '& .MuiAlert-action': {
                paddingTop: 0,
                alignItems: 'center'
              }
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleCloseMsg}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                  }
                }}
              >
                OK
              </Button>
            }
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {tms === 'error' ? 'Erro' :
                  tms === 'warning' ? 'Atenção' :
                    tms === 'success' ? 'Sucesso' : 'Informação'}
              </Typography>
              <Typography variant="body2">
                {msg}
              </Typography>
            </Box>
          </Alert>
        </Snackbar>
        {/* Paper/TableContainer sempre montados (mesmo antes da sessão carregar ou sem
            sessão aberta) — nunca troca pra uma árvore de componentes totalmente diferente.
            É o que garante que `tabelaRef` já existe desde a primeira renderização, pro
            cálculo de altura (useListagemAltura) medir a posição real da tabela uma única
            vez e acertar de primeira (mesma lógica da listagem de Produtos, que nunca esconde
            a tabela atrás de um estado condicional como esse). */}
        <Paper elevation={0} sx={{ ...paperListagemSx(theme), overflowX: 'hidden' }}>
            <TableContainer
              ref={tabelaRef}
              sx={{
                  ...tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage),
                  // CORREÇÃO: era overflowX:'hidden' — com tableLayout:'fixed' +
                  // width:'100%' a tabela nunca passava da largura do container mesmo
                  // (não tinha "conteúdo que não aparece", tinha colunas espremidas e
                  // texto vazando de uma célula pra outra). Trocado pra minWidth (ver
                  // abaixo): agora, num container mais estreito que a soma das colunas
                  // (ex.: celular), é a tabela que cresce e o overflow:auto (herdado de
                  // tableContainerListagemSx) que assume o scroll horizontal — sem
                  // reservar barra nenhuma em telas largas, onde a tabela cabe sozinha.
                }}
              >
                <Table size="small" sx={{ width: '100%', minWidth: smDown ? 0 : 800, tableLayout: 'fixed' }}>
                  {!listaCelular && (
                  <TableHead>
                    <TableRow>
                      <TableCell align='center' sx={{ width: '40px', px: 0.5 }}>Sel.</TableCell>
                      {!smDown && <TableCell sx={{ width: '108px' }}>Emissão</TableCell>}
                      {!smDown && <TableCell sx={{ width: '100px' }}>Número</TableCell>}
                      <TableCell>Parceiro</TableCell>
                      {!smDown && <TableCell sx={{ width: '120px' }}>Operação</TableCell>}
                      {!smDown && <TableCell sx={{ width: '118px' }}>CPF</TableCell>}
                      <TableCell align='right' sx={{ width: smDown ? '84px' : '100px' }}>Total</TableCell>
                      <TableCell align='center' sx={{ width: smDown ? '76px' : '88px' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  )}

                  {(carregandoSessao || isLoading) && rows.length === 0 && (
                    <TableBody>
                      <LinhasEsqueletoTabela
                        colunas={smDown ? [
                          { variant: 'rounded', width: 18, height: 18, align: 'center' },
                          { width: '75%' },
                          { width: '65%', align: 'right' },
                          { variant: 'rounded', width: '70%', height: 22, align: 'center' },
                        ] : [
                          { variant: 'rounded', width: 18, height: 18, align: 'center' },
                          { width: '70%' },
                          { width: '55%' },
                          { width: '75%' },
                          { variant: 'rounded', width: '70%', height: 22 },
                          { width: '70%' },
                          { width: '65%', align: 'right' },
                          { variant: 'rounded', width: '70%', height: 22, align: 'center' },
                        ]}
                      />
                    </TableBody>
                  )}
                  <TableBody>
                    {/* Lista sempre aparece, com ou sem sessão aberta — sem
                        sessão só a AÇÃO de faturar é bloqueada (ver checagem
                        de sessaoAtiva mais abaixo, no clique de faturar), não
                        a visualização do que está pendente. O aviso de
                        "bloqueado" já aparece no card de status lá em cima. */}
                    {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idmov}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nome || '?'}
                      titulo={row.nome}
                      detalhes={[dayjs(row.emissao).format('DD/MM/YY HH:mm'), row.numero ? `Nº ${row.numero}` : null, row.operacao]}
                      valor={moedaMobile(row.total)}
                      corValor="success.main"
                      etiqueta={row.tipo === 1 ? { rotulo: 'Entrada', cor: 'info' } : row.tipo === 3 ? { rotulo: 'Devolução', cor: 'warning' } : { rotulo: 'Venda', cor: 'success' }}
                      destaque={row.idmov === selectedId}
                      onClick={() => handleRowClick(row.idmov)}
                      acoes={(
                        <Checkbox
                          checked={row.idmov === selectedId}
                          onChange={() => handleSelectItem(row.idmov, row.idparceiro, row.nome, row.tpag, row.total, row.numero, row.emissao, row.tipo, row.cpf)}
                          value={row.idmov}
                          name="faturamento-checkbox"
                          size="small"
                        />
                      )}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row) => {
                      const isSelected = row.idmov === selectedId;
                      return (
                        <TableRow
                          key={row.idmov}
                          hover
                          selected={isSelected}
                          sx={{
                            cursor: 'pointer',
                            '&:last-child td': { borderBottom: 0 },
                            '& td': {
                              py: smDown ? 0.75 : 1.25,
                              px: smDown ? 0.75 : 1.5,
                              fontSize: smDown ? '0.75rem' : '0.8125rem',
                              borderBottom: `1px solid ${theme.torq.border.default}`,
                            },
                            backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.035),
                            },
                          }}
                          onClick={() => handleRowClick(row.idmov)}
                        >
                          <TableCell align='center' onClick={(e) => e.stopPropagation()} sx={{ px: '4px !important' }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectItem(
                                  row.idmov,
                                  row.idparceiro,
                                  row.nome,
                                  row.tpag,
                                  row.total,
                                  row.numero,
                                  row.emissao,
                                  row.tipo,
                                  row.cpf
                                );
                              }}
                              value={row.idmov}
                              name="faturamento-checkbox"
                              inputProps={{ 'aria-label': `Selecionar faturamento ${row.numero}` }}
                              size="small"
                              sx={{ p: 0.25 }}
                            />
                          </TableCell>

                          {!smDown && (
                            <TableCell>
                              <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                                {dayjs(row.emissao).format('DD/MM/YY HH:mm')}
                              </Typography>
                            </TableCell>
                          )}

                          {!smDown && (
                            <TableCell>
                              <Chip
                                label={row.numero || 'N/A'}
                                size="small"
                                sx={{
                                  height: '22px',
                                  fontSize: '0.6875rem',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 1.25 },
                                  bgcolor: theme.torq.surface.level2,
                                  color: theme.torq.text.secondary,
                                }}
                              />
                            </TableCell>
                          )}

                          <TableCell>
                            <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                              {row.nome}
                            </Typography>
                          </TableCell>

                          {!smDown && (
                            <TableCell>
                              <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                                {row.operacao || '-'}
                              </Typography>
                            </TableCell>
                          )}

                          {!smDown && (
                            <TableCell>
                              <Typography fontFamily="monospace" noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                                {formatarCPF(row.cpf)}
                              </Typography>
                            </TableCell>
                          )}

                          <TableCell align='right'>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'success.main' }}>
                              {Number(row.total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </Typography>
                          </TableCell>

                          <TableCell align='center'>
                            <Chip
                              label={
                                row.tipo === 1
                                  ? 'Entrada'
                                  : row.tipo === 3
                                    ? 'Devolução'
                                    : 'Venda'
                              }
                              size="small"
                              color={
                                row.tipo === 1
                                  ? 'info'
                                  : row.tipo === 3
                                    ? 'warning'
                                    : 'success'
                              }
                              variant="outlined"
                              sx={{
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                height: 20
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>

                  {rows.length === 0 && !isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={smDown ? 4 : 8} align="center" sx={{ py: 4 }}>
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <ReceiptLongIcon sx={{
                              fontSize: 35,
                              color: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.text.secondary, 0.4)
                                : 'text.secondary',
                              mb: 1
                            }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom fontSize="0.85rem">
                              {Environment.LISTAGEM_VAZIA}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontSize="0.7rem">
                              {busca ? 'Tente ajustar os filtros de busca' : 'Nenhum registro de faturamento encontrado'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  <TableFooter>
                    {isLoading && (
                      <TableRow>
                        <TableCell colSpan={smDown ? 4 : 8} sx={{ p: 0 }}>
                          <LinearProgress sx={{ height: '1.5px' }} />
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell colSpan={smDown ? 4 : 8} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                        <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                          <Typography variant='caption' color='text.secondary'>
                            {totalCount === 0
                              ? 'Nenhum registro encontrado'
                              : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} registro${totalCount !== 1 ? 's' : ''}`}
                          </Typography>

                          {totalCount > rowsPerPage && (
                            <Pagination
                              page={pagina}
                              count={totalPaginas}
                              onChange={(_, newPage) => setSearchParams({
                                tipo: tipoAba,
                                busca,
                                pagina: newPage.toString()
                              }, { replace: true })}
                              size="small"
                              shape="rounded"
                              showFirstButton
                              showLastButton
                              siblingCount={1}
                              boundaryCount={1}
                              sx={{
                                '& .MuiPaginationItem-root.Mui-selected': {
                                  backgroundColor: brand.menuBase,
                                  color: '#fff',
                                  '&:hover': { backgroundColor: '#012D3D' },
                                },
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>

              <DialogFaturameto
                id="detalhes"
                value=""
                open={openDialogFaturamento}
                status={true}
                movimentoData={movimentoData}
                onDados={(dados) => {

                }}
                onClose={() => setOpenDialogFaturamento(false)}
              />
              <DialogFaturar
                id={selectedId ?? ''}
                open={openDialogFaturar}
                parceiroNome={selectedNome}
                valorTotal={selectedTotal}
                idContaSessao={sessaoAtiva?.idconta ?? ''}
                numeroVenda={selectedNumeroDocumento}
                cpfCliente={formatarCPF(selectedCpf)}
                onClose={() => {
                  setOpenDialogFaturar(false);
                  refreshList();
                }}
              />
              <Dialogsendnfce
                selectedId={selectedId}
                selectedtpag={selecteTpag}
                open={openDialogPagamento}
                parceironome={selectedNome}
                valortotal={selectedTotal}
                numerodocumento={selectedNumeroDocumento}
                emissaodata={selectedEmissaoData}
                onClose={() => {
                  setOpenDialogPagamento(false);
                  refreshList();
                }}
              />
              <DialogNFE
                selectedId={selectedId}
                selectedtpag={selecteTpag}
                idpar={selectedParceiro}
                parceironome={selectedNome}
                valortotal={selectedTotal}
                numerodocumento={selectedNumeroDocumento}
                emissaodata={selectedEmissaoData}
                open={openDialognfe}
                onClose={() => {
                  setOpenDialognfe(false);
                  refreshList();
                }}
              /><Dialog
                open={openConfirmFaturar}
                onClose={() => setOpenConfirmFaturar(false)}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    p: 2,
                    minWidth: 360,
                    textAlign: 'center'
                  }
                }}
              >
                <DialogTitle sx={{ fontWeight: 700 }}>
                  Confirmar Faturamento
                </DialogTitle>

                <DialogContent>
                  <Typography sx={{ mb: 2 }}>
                    Deseja realmente faturar esta venda?
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Parceiro:</strong> {selectedNome}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Valor:</strong>{' '}
                      {selectedTotal.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      })}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => setOpenConfirmFaturar(false)}
                    >
                      Cancelar
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={confirmarFaturamento}
                    >
                      Confirmar
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>
        </Paper>
      </LayoutBaseDePagina>
      <DialogAbrirSessaoCaixa
        open={openDialogAbrirSessao}
        onClose={handleFecharDialogAbrirSessao}
      />
      <DialogFecharSessaoCaixa
        open={openDialogFecharSessao}
        idconta={sessaoAtiva?.idconta ?? ''}
        nomeConta={sessaoAtiva?.nomeConta}
        dataabertura={sessaoAtiva?.dataabertura}
        onClose={handleFecharDialogFecharSessao}
      />
    </>
  );
};
